import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useLenisLock } from "../../lib/useLenisLock";
import { ArrowUpRight, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

export const CaseModal = ({ caseItem, onOpenChange, onBook }) => {
  const [idx, setIdx] = useState(0);
  const open = !!caseItem;
  const gallery = caseItem?.gallery || (caseItem ? [{ src: caseItem.image, mobile: false }] : []);
  const cur = gallery[idx] || { src: "", mobile: false };
  const scrollRef = useRef(null);
  useLenisLock(open);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [idx]);

  useEffect(() => {
    setIdx(0);
  }, [caseItem]);

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % gallery.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + gallery.length) % gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, gallery.length]);

  if (!caseItem) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent
        data-testid="case-modal"
        className="max-w-5xl w-[96vw] p-0 bg-[var(--surface)] border-white/10 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">{caseItem.name}</DialogTitle>
        <DialogDescription className="sr-only">
          {caseItem.typeLabel} — {caseItem.sector}. {caseItem.solution}
        </DialogDescription>
        <div className="grid lg:grid-cols-5 min-w-0 w-full">
          {/* Gallery */}
          <div className="lg:col-span-3 relative bg-black min-w-0 overflow-hidden">
            <div className="relative h-[46vh] lg:h-[76vh]">
              <div className="h-full overflow-auto bg-black flex p-3" ref={scrollRef}>
                <img
                  key={cur.src}
                  src={cur.src}
                  alt={`${caseItem.name} captura ${idx + 1}`}
                  className={
                    cur.mobile
                      ? "m-auto h-auto w-auto max-w-[240px] lg:max-w-[330px] rounded-2xl shadow-2xl ring-1 ring-white/10"
                      : "m-auto h-auto w-full max-w-full rounded-lg"
                  }
                  data-testid="case-modal-image"
                />
              </div>
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() => setIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                    data-testid="case-modal-prev"
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full glass p-2 hover:bg-white/10 transition-colors duration-200"
                    aria-label="Anterior"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => setIdx((i) => (i + 1) % gallery.length)}
                    data-testid="case-modal-next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full glass p-2 hover:bg-white/10 transition-colors duration-200"
                    aria-label="Siguiente"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full glass px-3 py-1 text-xs text-white">
                    {idx + 1} / {gallery.length}
                  </span>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {gallery.map((g, i) => (
                  <button
                    key={g.src}
                    onClick={() => setIdx(i)}
                    data-testid={`case-modal-thumb-${i}`}
                    className={`h-16 w-28 shrink-0 rounded-lg overflow-hidden border transition-colors duration-200 ${
                      i === idx ? "border-[var(--magenta)]" : "border-white/10 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={g.src} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-2 p-6 sm:p-7 flex flex-col min-w-0">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="rounded-full bg-[var(--magenta)]/15 text-[var(--magenta)] px-3 py-1 text-xs font-medium">{caseItem.typeLabel}</span>
              <span className="rounded-full glass px-3 py-1 text-xs text-zinc-300">{caseItem.sector}</span>
            </div>
            <h3 className="font-display font-semibold text-2xl">{caseItem.name}</h3>

            <div className="mt-5 space-y-4 text-sm flex-1">
              <div>
                <p className="text-[var(--magenta)] font-medium mb-1">Problema</p>
                <p className="text-zinc-400 leading-relaxed">{caseItem.problem}</p>
              </div>
              <div>
                <p className="text-emerald-400 font-medium mb-1">Solución</p>
                <p className="text-zinc-300 leading-relaxed">{caseItem.solution}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              {caseItem.url && (
                <a
                  href={caseItem.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-modal-visit"
                  className="inline-flex items-center justify-center gap-2 rounded-full glass px-5 py-3 font-medium hover:border-white/25 transition-colors duration-300"
                >
                  Visitar sitio <ExternalLink size={16} />
                </a>
              )}
              <button
                onClick={() => {
                  onOpenChange(null);
                  onBook();
                }}
                data-testid="case-modal-book"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--magenta)] text-white font-semibold px-5 py-3 hover-lift hover:magenta-glow"
              >
                Quiero algo así <ArrowUpRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
